import axiosInstance from "@/api/axiosInstance";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";

import { ENDPOINTS } from "@/constants/api";
import { Competition } from "@/common/model/Competition";
import { EventSessionUser, EventUser } from "@/common/model/User";
import { Heat } from "@/common/model/Heat";
import { LycraColorConfig, DEFAULT_LYCRA_COLORS } from "@/common/model/LycraColorConfig";
import Config from "@/Config";
import ConfirmationDialog from "./ConfirmDialog";
import HeatDetail from "@/heat/HeatDetail";
import LycraColorConfigEditor from "@/components/LycraColorConfigEditor";
import { Button, Input, Card } from "@/components/ui";

const CompetitionDetails: React.FC = () => {
  const [eventId, setEventId] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [competition, setCompetition] = useState<Competition>();
  const customDateFormat = "YYYY-MM-DDTHH:mm:ss.SSS[Z][UTC]";
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [heatIdForEdit, setHeatIdForEdit] = useState(0);
  const [heatIdForDelete, setHeatIdForDelete] = useState(0);
  const [heatTimer, setHeatTimer] = useState(60);
  const [minHeight, setMinHeight] = useState(0.0);
  const [availableUsers, setAvailableUsers] = useState<EventUser[]>([]);
  const [allEventUsers, setAllEventUsers] = useState<EventUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedHeatId, setExpandedHeatId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCompetition, setEditedCompetition] = useState<Competition | null>(null);
  const [lycraColorConfig, setLycraColorConfig] = useState<LycraColorConfig[]>(DEFAULT_LYCRA_COLORS);
  const [isSavingColors, setIsSavingColors] = useState(false);
  const [showColorConfig, setShowColorConfig] = useState(false);

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const query = useQuery();
  const navigate = useNavigate();

  function loadCompetition(id: number = 0) {
    if (id === 0) {
      id = eventId;
    }
    // Fetch heats and users from the backend
    axiosInstance
      .get(`${ENDPOINTS.COMPETITIONS.DEFAULT}/${id}`)
      .then((response) => {
        setCompetition(response.data);
        // Store the competition name in session storage
        if (response.data && response.data.name) {
          sessionStorage.setItem("competitionName", response.data.name);
        }
        setHeatTimer(response.data?.attempttime);
        setMinHeight(response.data?.minheight);
        setAvailableUsers(response.data?.eventusers);
        setAllEventUsers(response.data?.eventusers);
      })
      .catch((error) => {
        console.error("Error fetching competition data:", error);
      });

    // Also fetch lycra color configuration
    axiosInstance
      .get(`${ENDPOINTS.COMPETITIONS.DEFAULT}/${id}/lycracolors`)
      .then((response) => {
        if (response.data && response.data.length > 0) {
          setLycraColorConfig(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching lycra color config:", error);
        // Use defaults on error
        setLycraColorConfig(DEFAULT_LYCRA_COLORS);
      });
  }

  async function saveLycraColorConfig() {
    setIsSavingColors(true);
    try {
      const response = await axiosInstance.put(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/lycracolors`,
        lycraColorConfig
      );
      if (response.status === 200) {
        setToastMessage("Lycra color configuration saved successfully!");
      }
    } catch (error) {
      console.error("Error saving lycra color config:", error);
      setToastMessage("Error saving lycra color configuration");
    } finally {
      setIsSavingColors(false);
    }
  }

  useEffect(() => {
    const id = query.get("id");
    if (id) {
      // Store the id in session storage
      sessionStorage.setItem("competitionId", id);
      setEventId(Number(id));
      loadCompetition(Number(id));
    } else {
      const storedId = sessionStorage.getItem("competitionId");
      if (storedId) {
        setEventId(Number(storedId));
        loadCompetition(Number(storedId));
      } else {
        // If no ID is found, redirect to the competitions list
        navigate("/competitions");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doDeleteHeat = async (heat: Heat) => {
    // Close the modal
    setShowDeleteModal(false);

    // API request to delete
    try {
      const response = await axiosInstance.delete(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${heat.eventid}/heat/${heat.id}`
      );
      if (response.status === 200) {
        console.log("Heat deleted successfully!");
        setToastMessage("Heat deleted successfully!");
      } else {
        console.error("Failed to delete heat.");
        setToastMessage("Failed to delete heat.");
      }
    } catch (error) {
      console.error("There was an error deleting the heat:", error);
      setToastMessage("There was an error deleting the heat:" + error);
    }
    // reload
    loadCompetition(eventId);
  };

  const createNewHeat = async (payload: any): Promise<Heat | undefined> => {
    const apiUrl = `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/heat`;

    try {
      const response = await axiosInstance.post(apiUrl, payload);
      console.log("Response:", response.data);

      loadCompetition(eventId);
      return response.data;
    } catch (error) {
      console.error("Error creating the heat:", error);
      return undefined;
    }
  };

  function toDate(dateValue: string | Date | number | undefined | null) {
    if (!dateValue) {
      return moment();
    }

    // Handle numeric timestamps (milliseconds)
    if (typeof dateValue === 'number') {
      // Check if it's a valid timestamp (not too far in the future or past)
      const date = moment(dateValue);
      if (date.isValid() && date.year() > 1900 && date.year() < 2100) {
        return date;
      }
      // If invalid, return current date
      console.warn('Invalid date timestamp:', dateValue);
      return moment();
    }

    if (typeof dateValue === 'string') {
      // First try to parse as ISO string
      const isoDate = moment(dateValue);
      if (isoDate.isValid()) {
        return isoDate;
      }
      
      // Then try with UTC replacement
      if (dateValue.includes('UTC')) {
        const utcDate = moment(dateValue.replace('[UTC]', ''));
        if (utcDate.isValid()) {
          return utcDate;
        }
      }
      
      // Try custom format
      const customDate = moment(dateValue, customDateFormat);
      if (customDate.isValid()) {
        return customDate;
      }
      
      // Default fallback
      console.warn('Could not parse date string:', dateValue);
      return moment();
    }

    if (dateValue instanceof Date) {
      return moment(dateValue);
    }

    return moment();
  }

  const doActivateHeat = async (heat: Heat) => {
    const apiUrl = `${ENDPOINTS.COMPETITIONS.LIVEDATA}/${eventId}/setactiveheat/${heat.heatnr}`;
    try {
      const response = await axiosInstance.get(apiUrl);
      console.log("Response:", response.data);
      loadCompetition(eventId);
    } catch (error) {
      console.error("Error activating heat:", error);
    }
  };

  function setModalEditOpen(state: boolean) {
    setIsModalOpen(state);
  }

  function disableHeadEdit(state: boolean) {
    setHeatIdForEdit(-1);
  }

  function getDisplayNameForEventUser(eventuseridsource: number) {
    return allEventUsers.find((user) => user.id === eventuseridsource);
  }

  function getRelevantLastPing(
    eventSessionUser: EventSessionUser
  ): { lastping: Date | null; source: 'user' | 'device'; deviceName?: string } {
    // If a device is assigned (eventuseridsource exists and differs from user)
    if (eventSessionUser.eventuseridsource &&
        eventSessionUser.eventuseridsource !== eventSessionUser.eventuser.id) {

      // Find the device in allEventUsers
      const device = allEventUsers.find(u => u.id === eventSessionUser.eventuseridsource);

      if (device) {
        return {
          lastping: device.lastping,
          source: 'device',
          deviceName: device.displayname
        };
      }
    }

    // Fallback to user's lastping
    return {
      lastping: eventSessionUser.eventuser.lastping,
      source: 'user'
    };
  }

  function getPingStatus(lastping: Date | null | undefined | any): {
    color: string;
    bgColor: string;
    showCircle: boolean;
    displayText: string;
    minutesAgo: number | null;
  } {
    // Check for null/undefined
    if (!lastping) {
      return {
        color: '#ef4444', // red
        bgColor: '#fee2e2', // red-100
        showCircle: true,
        displayText: 'Offline',
        minutesAgo: null
      };
    }

    const now = moment();

    // Try to parse the date - handle different formats
    let lastPingMoment: moment.Moment;
    const lastpingStr = String(lastping);

    if (typeof lastping === 'string') {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    } else if (typeof lastping === 'number') {
      lastPingMoment = moment(lastping);
    } else {
      // Handle Date object or other types
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    }

    // Check if the moment is valid
    if (!lastPingMoment.isValid()) {
      return {
        color: '#ef4444', // red
        bgColor: '#fee2e2', // red-100
        showCircle: true,
        displayText: 'Invalid date',
        minutesAgo: null
      };
    }

    const minutesAgo = now.diff(lastPingMoment, 'minutes');
    const secondsAgo = now.diff(lastPingMoment, 'seconds');

    if (minutesAgo < 1) {
      // Green - online, recent ping
      return {
        color: '#10b981', // green-500
        bgColor: '#d1fae5', // green-100
        showCircle: true,
        displayText: `${secondsAgo} sec ago`,
        minutesAgo: 0
      };
    } else if (minutesAgo <= 3) {
      // Yellow - warning
      return {
        color: '#f59e0b', // amber-500
        bgColor: '#fef3c7', // amber-100
        showCircle: true,
        displayText: `${minutesAgo} min ago`,
        minutesAgo
      };
    } else {
      // Red - offline
      return {
        color: '#ef4444', // red-500
        bgColor: '#fee2e2', // red-100
        showCircle: true,
        displayText: `${minutesAgo} min ago`,
        minutesAgo
      };
    }
  }

  function getExactPingTime(lastping: Date | null | undefined | any): string {
    if (!lastping) {
      return 'Never';
    }

    const lastpingStr = String(lastping);
    let lastPingMoment: moment.Moment;

    if (typeof lastping === 'string') {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    } else if (typeof lastping === 'number') {
      lastPingMoment = moment(lastping);
    } else {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    }

    if (!lastPingMoment.isValid()) {
      return 'Invalid date';
    }

    return lastPingMoment.format("DD-MM HH:mm:ss");
  }

  const updateHeat = async (payload: any): Promise<Heat | undefined> => {
    const apiUrl = `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}/heat/${payload.id}`;

    // Clean the payload to avoid backend deserialization issues
    // Remove the full user objects and keep only IDs
    const cleanedPayload = {
      ...payload,
      eventSessionUsers: payload.eventSessionUsers?.map((sessionUser: any) => ({
        id: sessionUser.id,
        eventuser: {
          id: sessionUser.eventuser.id,
          // Don't include the user object to avoid deserialization issues
          displayname: sessionUser.eventuser.displayname,
          localid: sessionUser.eventuser.localid,
          issource: sessionUser.eventuser.issource
        },
        eventsessionid: sessionUser.eventsessionid,
        eventuseridsource: sessionUser.eventuseridsource,
        lycracolor: sessionUser.lycracolor
      }))
    };

    try {
      const response = await axiosInstance.put(apiUrl, cleanedPayload);
      console.log("Response:", response.data);

      loadCompetition(eventId);
      return response.data;
    } catch (error) {
      console.error("Error updating heat:", error);
      return undefined;
    }
  };

  async function activateUserForHeat(
    competitionId: number,
    eventSessionUser: EventSessionUser,
    lycraColor: string,
    attempttime: number
  ) {
    const apiUrl = `${Config.BACKEND_URL}scoring/activaterider/${competitionId}/${eventSessionUser.eventuser.localid}?flagcolor=${lycraColor}&seconds=${attempttime}`;
    try {
      const response = await axiosInstance.get(apiUrl);
      console.log("Response activateUserForHeat:", response.data);
      alert("User " + eventSessionUser.eventuser.displayname + " Activated");
      return response.data;
    } catch (error) {
      console.error("Error activating user for heat:", error);
    }
  }

  async function updateEvent() {
    const apiUrl = `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}`;
    try {
      // Only send the fields that need to be updated
      // Don't send heatList and eventusers to avoid deserialization issues
      const updatePayload = {
        id: eventId,
        name: competition?.name,
        description: competition?.description,
        location: competition?.location,
        startDate: competition?.startDate,
        endDate: competition?.endDate,
        attempttime: heatTimer,
        minheight: minHeight,
        livedataendpoint: competition?.livedataendpoint
      };
      
      const response = await axiosInstance.put(apiUrl, updatePayload);
      console.log("Response:", response.data);
      setToastMessage("Competition settings updated successfully!");
      loadCompetition(eventId);
      return response.data;
    } catch (error) {
      console.error("Error updating competition:", error);
      setToastMessage("Error updating competition settings");
    }
  }

  const handleEditCompetition = () => {
    setEditedCompetition({
      ...competition!,
    });
    setIsEditing(true);
  };

  const handleSaveCompetitionDetails = async () => {
    if (!editedCompetition) return;

    // Create a copy of the competition with properly formatted dates
    // Remove heatList and eventusers to avoid backend deserialization issues
    const { heatList, eventusers, ...competitionBasicFields } = editedCompetition;
    const competitionToSave = {
      ...competitionBasicFields,
      // Add time component to dates (set to noon UTC to avoid timezone issues)
      startDate: editedCompetition.startDate ? 
        toDate(editedCompetition.startDate).format("YYYY-MM-DDT12:00:00.000Z") : 
        undefined,
      endDate: editedCompetition.endDate ? 
        toDate(editedCompetition.endDate).format("YYYY-MM-DDT12:00:00.000Z") : 
        undefined
    };

    try {
      const response = await axiosInstance.put(
        `${ENDPOINTS.COMPETITIONS.DEFAULT}/${eventId}`,
        competitionToSave
      );

      if (response.status === 200) {
        setCompetition(response.data);
        setIsEditing(false);
        setToastMessage("Competition details updated successfully!");

        // Update the competition name in session storage if it changed
        if (response.data && response.data.name) {
          sessionStorage.setItem("competitionName", response.data.name);
        }
      }
    } catch (error) {
      console.error("Error updating competition details:", error);
      setToastMessage("Error updating competition details");
    }
  };

  // Filter heats based on search term
  const filteredHeats = competition?.heatList?.filter(heat => 
    heat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    heat.heatnr.toString().includes(searchTerm)
  );

  const toggleHeatExpansion = (heatId: number) => {
    if (expandedHeatId === heatId) {
      setExpandedHeatId(null);
    } else {
      setExpandedHeatId(heatId);
    }
  };

  const headerTextStyle = {
    // No text shadow needed with dark text on light background
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Competition Header Card - Modern Design */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-blue-50 to-blue-100 flex justify-between items-start">
          <div className="flex-1 mr-4">
            {isEditing ? (
              <Input
                className="text-black text-2xl font-bold mb-2 bg-white border-2 border-blue-300 w-full"
                value={editedCompetition?.name || ""}
                onChange={(e) => setEditedCompetition({...editedCompetition!, name: e.target.value})}
                placeholder="Competition Name"
              />
            ) : (
              <h1 className="text-3xl font-bold mb-1 text-blue-900">{competition?.name}</h1>
            )}
            <div className="flex items-center gap-2 text-blue-800">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                ID: {eventId}
              </span>
              {isEditing ? (
                <Input
                  className="text-black bg-white border-2 border-blue-300 flex-1"
                  value={editedCompetition?.location || ""}
                  onChange={(e) => setEditedCompetition({...editedCompetition!, location: e.target.value})}
                  placeholder="Location"
                />
              ) : (
                competition?.location && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    {competition.location}
                  </span>
                )
              )}
            </div>

            {/* Display description and livedataendpoint in non-editing mode */}
            {!isEditing && competition?.description && (
              <p className="mt-2 text-gray-700 max-w-2xl">{competition.description}</p>
            )}
            {!isEditing && competition?.livedataendpoint && (
              <div className="mt-2">
                <span className="text-sm font-medium text-gray-600">Live Data: </span>
                <a 
                  href={competition.livedataendpoint} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  {competition.livedataendpoint}
                </a>
              </div>
            )}

            {isEditing && (
              <>
                <div className="mt-2 w-full">
                  <label className="block text-sm font-medium text-blue-800 mb-1">Description</label>
                  <textarea
                    className="text-black bg-white border-2 border-blue-300 w-full rounded-md p-2"
                    value={editedCompetition?.description || ""}
                    onChange={(e) => setEditedCompetition({...editedCompetition!, description: e.target.value})}
                    placeholder="Description"
                    rows={3}
                  />
                </div>
                <div className="mt-2 w-full">
                  <label className="block text-sm font-medium text-blue-800 mb-1">Live Data Endpoint</label>
                  <Input
                    className="text-black bg-white border-2 border-blue-300 w-full"
                    value={editedCompetition?.livedataendpoint || ""}
                    onChange={(e) => setEditedCompetition({...editedCompetition!, livedataendpoint: e.target.value})}
                    placeholder="https://example.com/livedata"
                    type="url"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            {isEditing ? (
              <div className="flex flex-col gap-2 items-end">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-blue-800 whitespace-nowrap">Start:</label>
                    <Input
                      type="date"
                      className="text-black bg-white border-2 border-blue-300 w-32"
                      value={editedCompetition?.startDate ? toDate(editedCompetition.startDate).format("YYYY-MM-DD") : ""}
                      onChange={(e) => setEditedCompetition({
                        ...editedCompetition!, 
                        startDate: e.target.value
                      })}
                    />
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <label className="text-sm text-blue-800 whitespace-nowrap">End:</label>
                    <Input
                      type="date"
                      className="text-black bg-white border-2 border-blue-300 w-32"
                      value={editedCompetition?.endDate ? toDate(editedCompetition.endDate).format("YYYY-MM-DD") : ""}
                      onChange={(e) => setEditedCompetition({
                        ...editedCompetition!, 
                        endDate: e.target.value
                      })}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm font-medium text-blue-800">
                {competition?.startDate ? toDate(competition.startDate).format("DD-MM-YYYY") : "N/A"} - {competition?.endDate ? toDate(competition.endDate).format("DD-MM-YYYY") : "N/A"}
              </div>
            )}

            <div className="flex gap-2 mt-2">
              {isEditing ? (
                <>
                  <Button 
                    onClick={handleSaveCompetitionDetails}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    size="sm"
                  >
                    Save Details
                  </Button>
                  <Button 
                    onClick={() => setIsEditing(false)}
                    variant="secondary"
                    className="bg-gray-200 text-gray-800"
                    size="sm"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={handleEditCompetition}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  size="sm"
                >
                  Edit Details
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white p-4 border-t border-blue-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <label className="font-medium text-gray-700 whitespace-nowrap">Heat Timer:</label>
              <Input
                className="flex-1 border-2 border-gray-300 text-gray-900"
                type="number"
                value={heatTimer}
                onChange={(e) => setHeatTimer(e.target.valueAsNumber)}
              />
              <span className="text-gray-500 text-sm">sec</span>
            </div>

            <div className="flex items-center gap-2">
              <label className="font-medium text-gray-700 whitespace-nowrap">Min Height:</label>
              <Input
                className="flex-1 border-2 border-gray-300 text-gray-900"
                type="number"
                step="0.01"
                value={minHeight}
                onChange={(e) => setMinHeight(e.target.valueAsNumber)}
              />
              <span className="text-gray-500 text-sm">m</span>
            </div>

            <div className="flex justify-end">
              <Button 
                onClick={() => updateEvent()}
                className="bg-green-600 hover:bg-green-700"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Lycra Color Configuration Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div
          className="p-4 bg-gray-50 border-b border-gray-200 cursor-pointer flex justify-between items-center"
          onClick={() => setShowColorConfig(!showColorConfig)}
        >
          <div>
            <h2 className="font-medium text-gray-700">Lycra Color Configuration</h2>
            <p className="text-sm text-gray-500">Configure the colors and their display order for this competition</p>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform ${showColorConfig ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {showColorConfig && (
          <div className="p-4">
            <LycraColorConfigEditor
              config={lycraColorConfig}
              onChange={setLycraColorConfig}
              onSave={saveLycraColorConfig}
              isSaving={isSavingColors}
            />
          </div>
        )}
      </div>

      {/* Heats Section with Search */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div className="flex-1 w-full md:w-auto">
            <Input
              type="text"
              placeholder="Search heats by name or number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border-2 border-gray-300"
            />
          </div>
          <Button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
          >
            Add New Heat
          </Button>
        </div>

        {/* Compact Heat List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="py-2 px-4 bg-gray-50 border-b border-gray-200">
            <h2 className="font-medium text-gray-700">Heats ({filteredHeats?.length || 0})</h2>
          </div>

          {(!filteredHeats || filteredHeats.length === 0) ? (
            <div className="text-center py-8 bg-gray-50">
              <p className="text-gray-500">
                {searchTerm ? "No heats match your search" : "No heats found. Create your first heat using the button above."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredHeats
                .sort((a: Heat, b: Heat) => b.heatnr - a.heatnr)
                .map((heat: Heat) => (
                  <div key={heat.id} className="border-l-4 transition-all" style={{ borderLeftColor: heat.active ? '#10b981' : 'transparent' }}>
                    <div 
                      className="p-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleHeatExpansion(heat.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-bold text-gray-800">
                            #{heat.heatnr}
                          </div>
                          <div className="font-medium text-gray-700">
                            {heat.name}
                          </div>
                          {heat.localid && (
                            <div className="text-xs text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded">
                              ID: {heat.localid}
                            </div>
                          )}
                          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            heat.active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {heat.active ? 'Active' : 'Inactive'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {heat.eventSessionUsers.length} participants
                          </div>
                          {heat.startdatetime && (
                            <div className="text-xs text-gray-600">
                              🕐 {moment(heat.startdatetime).format('HH:mm')}
                            </div>
                          )}
                          {heat.enddatetime && (
                            <div className="text-xs text-gray-600">
                              🏁 {moment(heat.enddatetime).format('HH:mm')}
                            </div>
                          )}
                          {heat.startdatetime && heat.enddatetime && (
                            <div className="text-xs text-gray-600 font-medium">
                              ({(() => {
                                const start = moment(heat.startdatetime);
                                const end = moment(heat.enddatetime);
                                const duration = moment.duration(end.diff(start));
                                const minutes = Math.floor(duration.asMinutes());
                                const seconds = duration.seconds();
                                return `${minutes}m ${seconds}s`;
                              })()})
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setHeatIdForEdit(heat.id);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteModal(true);
                              setHeatIdForDelete(heat.id);
                            }}
                          >
                            Delete
                          </Button>
                          <Button
                            size="sm"
                            className={heat.active 
                              ? "bg-red-500 hover:bg-red-600 text-white" 
                              : "bg-green-600 hover:bg-green-700 text-white"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (heat.active) {
                                const heatNr = heat.heatnr;
                                heat.heatnr = -1;
                                doActivateHeat(heat);
                                heat.heatnr = heatNr;
                              } else {
                                doActivateHeat(heat);
                              }
                            }}
                          >
                            {heat.active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Heat Details */}
                    {expandedHeatId === heat.id && (
                      <div className="bg-gray-50 p-3 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Participants</h4>
                        {heat.eventSessionUsers.length === 0 ? (
                          <p className="text-sm text-gray-500 italic">No users in this heat</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {heat.eventSessionUsers.map((eventSessionUser: EventSessionUser) => (
                              <div 
                                key={eventSessionUser.eventuser.id} 
                                className="bg-white rounded border border-gray-200 p-2 flex items-center justify-between"
                              >
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-4 h-4 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: eventSessionUser.lycracolor || "#000000" }}
                                  ></div>
                                  <div className="overflow-hidden">
                                    <div className="font-medium text-gray-800 truncate">
                                      {eventSessionUser.eventuser.displayname}
                                    </div>

                                    {eventSessionUser.eventuseridsource &&
                                     eventSessionUser.eventuseridsource !== eventSessionUser.eventuser.id && (
                                      <div className="text-xs text-gray-500 truncate">
                                        Device: {getDisplayNameForEventUser(eventSessionUser.eventuseridsource)?.displayname}
                                      </div>
                                    )}

                                    <div className="text-xs space-y-0.5">
                                      {(() => {
                                        const pingInfo = getRelevantLastPing(eventSessionUser);
                                        const deviceStatus = getPingStatus(pingInfo.lastping);
                                        const userStatus = getPingStatus(eventSessionUser.eventuser.lastping);
                                        const deviceExactTime = getExactPingTime(pingInfo.lastping);
                                        const userExactTime = getExactPingTime(eventSessionUser.eventuser.lastping);

                                        return (
                                          <>
                                            {/* Device Last Ping (when device is used) */}
                                            {pingInfo.source === 'device' && (
                                              <div
                                                className="flex items-center gap-1.5 cursor-help"
                                                title={`Device last ping: ${deviceExactTime}`}
                                              >
                                                <div
                                                  className="w-3 h-3 rounded-full flex-shrink-0 flex items-center justify-center"
                                                  style={{ backgroundColor: deviceStatus.bgColor }}
                                                >
                                                  <div
                                                    className="w-1.5 h-1.5 rounded-full"
                                                    style={{ backgroundColor: deviceStatus.color }}
                                                  ></div>
                                                </div>
                                                <span
                                                  className="font-medium truncate"
                                                  style={{ color: deviceStatus.color }}
                                                >
                                                  Device: {deviceStatus.displayText}
                                                </span>
                                              </div>
                                            )}

                                            {/* User Last Ping */}
                                            <div
                                              className="flex items-center gap-1.5 cursor-help"
                                              title={`User last ping: ${userExactTime}`}
                                            >
                                              {pingInfo.source === 'device' && (
                                                <div
                                                  className="w-3 h-3 rounded-full flex-shrink-0 flex items-center justify-center"
                                                  style={{ backgroundColor: userStatus.bgColor }}
                                                >
                                                  <div
                                                    className="w-1.5 h-1.5 rounded-full"
                                                    style={{ backgroundColor: userStatus.color }}
                                                  ></div>
                                                </div>
                                              )}
                                              {pingInfo.source !== 'device' && (
                                                <div
                                                  className="w-3 h-3 rounded-full flex-shrink-0 flex items-center justify-center"
                                                  style={{ backgroundColor: userStatus.bgColor }}
                                                >
                                                  <div
                                                    className="w-1.5 h-1.5 rounded-full"
                                                    style={{ backgroundColor: userStatus.color }}
                                                  ></div>
                                                </div>
                                              )}
                                              <span
                                                className={`truncate ${pingInfo.source === 'device' ? 'text-gray-400' : 'font-medium'}`}
                                                style={{
                                                  color: pingInfo.source === 'device'
                                                    ? '#9ca3af'
                                                    : userStatus.color
                                                }}
                                              >
                                                {pingInfo.source === 'device' ? 'User: ' : 'Last: '}
                                                {userStatus.displayText}
                                              </span>
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>

                                {heat.active && (
                                  <Button
                                    size="sm"
                                    onClick={() => activateUserForHeat(
                                      competition?.id || 0,
                                      eventSessionUser,
                                      eventSessionUser.lycracolor || "#000000",
                                      heatTimer
                                    )}
                                  >
                                    Activate
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Heat Timing Information */}
                        {(heat.localid || heat.startdatetime || heat.enddatetime) && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Heat Timing</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              {heat.localid && (
                                <div className="bg-white rounded border border-gray-200 p-2">
                                  <div className="text-xs text-gray-500 mb-1">External Heat ID</div>
                                  <div className="font-mono font-medium text-blue-600">{heat.localid}</div>
                                </div>
                              )}
                              {heat.startdatetime && (
                                <div className="bg-white rounded border border-gray-200 p-2">
                                  <div className="text-xs text-gray-500 mb-1">Start Time (UTC)</div>
                                  <div className="font-medium text-gray-800">
                                    {moment(heat.startdatetime).format('YYYY-MM-DD HH:mm:ss')}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {moment(heat.startdatetime).fromNow()}
                                  </div>
                                </div>
                              )}
                              {heat.enddatetime && (
                                <div className="bg-white rounded border border-gray-200 p-2">
                                  <div className="text-xs text-gray-500 mb-1">End Time (UTC)</div>
                                  <div className="font-medium text-gray-800">
                                    {moment(heat.enddatetime).format('YYYY-MM-DD HH:mm:ss')}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {moment(heat.enddatetime).fromNow()}
                                  </div>
                                </div>
                              )}
                              {heat.startdatetime && heat.enddatetime && (
                                <div className="bg-white rounded border border-gray-200 p-2">
                                  <div className="text-xs text-gray-500 mb-1">Duration</div>
                                  <div className="font-medium text-gray-800">
                                    {(() => {
                                      const start = moment(heat.startdatetime);
                                      const end = moment(heat.enddatetime);
                                      const duration = moment.duration(end.diff(start));
                                      const minutes = Math.floor(duration.asMinutes());
                                      const seconds = duration.seconds();
                                      return `${minutes} minutes ${seconds} seconds`;
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {heatIdForEdit === heat.id && (
                      <HeatDetail
                        eventId={eventId}
                        doCreate={false}
                        heat={heat}
                        onSave={updateHeat}
                        showDialog={disableHeadEdit}
                        availableUsersInput={availableUsers}
                        headerText={"Update Heat:"}
                        availableColors={lycraColorConfig}
                      />
                    )}

                    {heatIdForDelete === heat.id && (
                      <ConfirmationDialog
                        open={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={(passedHeat) => doDeleteHeat(passedHeat)}
                        passedHeat={heat}
                      />
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <HeatDetail
          eventId={eventId}
          doCreate={true}
          heat={{
            heatnr: 0,
            id: 0,
            eventid: eventId,
            name: "",
            active: false,
            eventSessionUsers: [],
          }}
          onSave={createNewHeat}
          showDialog={setModalEditOpen}
          availableUsersInput={availableUsers}
          headerText={"Create a new Heat:"}
          availableColors={lycraColorConfig}
        />
      )}

      {/* Toast message could be implemented here */}
    </div>
  );
};

export default CompetitionDetails;
